import { ContestLeaderboard } from './ContestLeaderboard';
import { BestRunAccountGated } from './BestRun';
import React, { useEffect, useState } from 'react';
import styles from './ContestLeaderboardPage.module.scss';
import {
  DEFAULT_LEADERBOARD_GAME_TYPE,
  getContestConfig,
  isValidLeaderboardGameType,
} from './LeaderboardGameLogic';
import { Navigate, useParams } from 'react-router-dom';
import { routes } from '../../routing/routes';
import { useCountdown } from '../../hooks/useCountdown';
import { Countdown } from '../Countdown/Countdown';
import cls from 'classnames';
import {
  Contest,
  Game,
  isArchivedLeaderboard,
  Leaderboard,
  useContestContext,
  useLeaderboard,
} from 'contexts/ContestContext';
import { Switch, SwitchItem } from 'components/Switch/Switch';
import Spinner from 'components/Spinner';
import { isFuture } from 'date-fns';
import { ContestOverview } from './ContestOverview';
import { Egg } from 'components/Egg/Egg';

export function ContestLeaderboardRoute() {
  const { type: gameType } = useParams();
  const { data: games } = useContestContext();
  const [selectedGame, setSelectedGame] = useState<Game | undefined>();
  // always defaulting to the first selected leaderboard for now
  const selectedLeaderboard = selectedGame?.leaderboards[0];
  // this contest becomes undefined when switching games, we want to
  // default the selected contest to the first contest in the leaderboard if available
  const [manuallySelectedContest, setSelectedContest] = useState<Contest | undefined>();

  const sortedContests = selectedLeaderboard
    ? [...selectedLeaderboard.contests].sort((a, b) => b.version - a.version)
    : undefined;

  const selectedContest = manuallySelectedContest ?? sortedContests?.[0];

  function setGameType(type: string) {
    // doing it like this so changing the route don't trigger a page refresh
    window.history.replaceState(null, type, routes.leaderboard.page({ type }));
    setSelectedGame(games?.find((x) => x.type === type));
    // contests also reset with the change of games
    setSelectedContest(undefined);
  }

  function switchContest(version: number) {
    if (!selectedLeaderboard || !sortedContests) {
      return;
    }
    const contest = sortedContests.find((contest) => contest.version === version);
    if (!contest) {
      alert(`No contest #${contest} found`);
      return;
    }
    setSelectedContest(contest);
  }

  useEffect(() => {
    if (games) {
      setSelectedGame(games.find((game) => game.type === gameType));
    }
  }, [games, gameType]);

  if (!gameType || !isValidLeaderboardGameType(gameType)) {
    return (
      <Navigate replace to={routes.leaderboard.page({ type: DEFAULT_LEADERBOARD_GAME_TYPE })} />
    );
  }

  return games && selectedGame && selectedLeaderboard ? (
    <Egg>
      <ContestLeaderboardPage
        games={games}
        contests={sortedContests}
        selectedLeaderboard={selectedLeaderboard}
        selectedContest={selectedContest}
        selectedGame={selectedGame}
        switchType={setGameType}
        switchContest={switchContest}
      />
    </Egg>
  ) : (
    <Egg>
      <div className={styles.spinner}>
        <Spinner color="white" width={50} height={50} />
      </div>
    </Egg>
  );
}

function ContestLeaderboardPage({
  games,
  selectedGame,
  selectedContest,
  selectedLeaderboard,
  switchType,
  contests,
  switchContest,
}: ContestLeaderboardPageProps) {
  const type = selectedGame.type;
  const { icon, cta } = getContestConfig(type);

  const version = selectedContest?.version ?? 0;
  const latestVersion = selectedLeaderboard?.latestVersion;

  const data = useLeaderboard(type, {
    version,
    latestVersion,
    gameId: selectedGame.id,
    contestName: selectedLeaderboard.name,
  });

  function attemptPageSwitch() {
    // we don't want to attempt to load
    // if we haven't finished loading already
    if (data.isFetching || !data.hasNextPage) {
      return;
    }
    data.fetchNextPage();
  }

  const onPillSwitchChange = (item: SwitchItem) => switchType(item.id);

  return (
    <div>
      <div className={styles.contestTitle}>
        <h1 className={styles.unsetMargin}>Contest</h1>
        <Switch
          items={games.map((game) => ({
            id: game.type,
            children: game.name,
          }))}
          initialSelection={type}
          onChange={onPillSwitchChange}
        />
      </div>
      <div className={styles.contest}>
        {selectedLeaderboard && selectedContest && contests && contests.length > 0 && (
          <ContestOverview
            contests={contests}
            selectedContest={selectedContest}
            onContestSelect={(item) => switchContest(item)}
            title={selectedLeaderboard.title}
            description={selectedLeaderboard.description}
            game={selectedGame.name}
            icon={icon}
            cta={cta}
          />
        )}
        <div className={styles.leaderboard}>
          <div className={styles.leaderboardTitle}>
            <h1 className="subheading-large">Leaderboard</h1>
            {selectedContest && <ContestEnd end={selectedContest.endDate} />}
          </div>
          {data.isFetched && data ? (
            <div>
              <BestRunAccountGated
                bestRun={data.bestRun}
                gameType={type}
                hide={isArchivedLeaderboard(version, latestVersion)}
              />
              <ContestLeaderboard
                gameType={type}
                data={data.runs ?? []}
                onLoadMore={() => {
                  attemptPageSwitch();
                }}
              />
            </div>
          ) : (
            <Spinner color="white" width={50} height={50} />
          )}
        </div>
      </div>
    </div>
  );
}

export type ContestLeaderboardPageProps = {
  games: Game[];
  contests?: Contest[];
  selectedGame: Game;
  selectedLeaderboard: Leaderboard;
  selectedContest?: Contest;
  switchType: (type: string) => void;
  switchContest(contestId: number): void;
};

function ContestEnd({ end }: ContestEndTimerProps) {
  return (
    <div>
      {isFuture(end) ? (
        <>
          <h2 className={cls('small-title', styles.endTimerLabel)}>Ends in:</h2>
          <ContestEndTimer end={end} />
        </>
      ) : (
        <h2 className={cls('small-title', styles.contestEndedLabel)}>Contest Ended</h2>
      )}
    </div>
  );
}

function ContestEndTimer({ end }: ContestEndTimerProps) {
  const countdown = useCountdown(end.getTime());

  return <Countdown countdown={countdown} />;
}

type ContestEndTimerProps = {
  end: Date;
};
